<template>
  <div class="ev-root" :style="{ '--ev-accent': brand.accent }">
    <!-- Top bar (white-label: brand name / tagline / accent come from ?brand=&tag=&accent= query params) -->
    <header class="ev-top">
      <div class="ev-top-inner">
        <span class="ev-brand">{{ brand.name }}</span>
        <span class="ev-brand-tag">{{ brand.tag }}</span>
        <button class="ev-brand-edit" title="Customize letterhead" @click="openBrandEdit">✎</button>
        <button v-if="signedIn" class="ev-signout" @click="signOut">Sign out</button>
      </div>
      <div v-if="brandEditing" class="ev-brand-editor">
        <input class="ev-input ev-be-in" v-model="brandForm.name" placeholder="Company / letterhead name" maxlength="40" />
        <input class="ev-input ev-be-in" v-model="brandForm.tag" placeholder="Tagline" maxlength="60" />
        <input class="ev-input ev-be-in ev-be-acc" v-model="brandForm.accent" placeholder="Accent hex (1E63FF)" maxlength="7" />
        <button class="ev-mini ev-pdf" @click="saveBrand">Save</button>
        <button class="ev-mini" @click="brandEditing = false">Cancel</button>
        <button class="ev-mini ev-del" @click="resetBrand">Reset</button>
      </div>
    </header>

    <!-- ============ NOT SIGNED IN: hero + phone login ============ -->
    <section v-if="!signedIn" class="ev-auth">
      <div class="ev-auth-card">
        <h1 class="ev-h1">What's your home worth?</h1>
        <p class="ev-sub">Get a real, comps-based estimate for any address — and save every
          property you check. Sign in with your phone to begin.</p>

        <!-- Step 1: phone -->
        <div v-if="authStep === 'phone'" class="ev-form">
          <label class="ev-label">Mobile number</label>
          <div class="ev-phone-row">
            <span class="ev-ext">{{ ext }}</span>
            <input ref="phoneInput" class="ev-input" v-model="phone" type="tel"
              inputmode="tel" placeholder="(555) 123-4567" @keyup.enter="sendCode" />
          </div>
          <button class="ev-btn" :disabled="busy || phone.replace(/\D/g, '').length < 7" @click="sendCode">
            {{ busy ? 'Sending…' : 'Send code' }}
          </button>
        </div>

        <!-- Step 2: code -->
        <div v-else class="ev-form">
          <label class="ev-label">Enter the 6-digit code we texted to {{ ext }} {{ phone }}</label>
          <input ref="codeInput" class="ev-input ev-code" v-model="code" type="tel"
            inputmode="numeric" maxlength="6" placeholder="••••••" @keyup.enter="verifyCode" />
          <button class="ev-btn" :disabled="busy || code.replace(/\D/g, '').length < 6" @click="verifyCode">
            {{ busy ? 'Verifying…' : 'Verify & continue' }}
          </button>
          <button class="ev-link" @click="authStep = 'phone'; code = ''">Use a different number</button>
        </div>

        <p v-if="authError" class="ev-error">{{ authError }}</p>
      </div>
    </section>

    <!-- ============ SIGNED IN: dashboard ============ -->
    <section v-else class="ev-dash">
      <!-- New address -->
      <div class="ev-new">
        <h2 class="ev-h2">Value an address</h2>
        <div class="ev-new-row">
          <GmapAutocomplete ref="evAddr" class="ev-input"
            placeholder="Start typing an address…"
            :options="{ types: ['address'], componentRestrictions: { country: ['us', 'ca'] } }"
            @place_changed="onPlaceChanged" @input.native="onAddrInput" />
          <button class="ev-btn ev-btn-go" :disabled="submitting || !addrSelected || newAddress.trim().length < 5" @click="valueAddress">
            {{ submitting ? 'Adding…' : 'Get estimate' }}
          </button>
        </div>
        <p class="ev-hint">Add as many as you like — each estimate runs in the background and fills in below.</p>
        <p v-if="valueError" class="ev-error">{{ valueError }}</p>
      </div>

      <!-- Saved valuations -->
      <h2 class="ev-h2 ev-saved-h">Your properties <span v-if="entries.length" class="ev-count">{{ entries.length }}</span></h2>

      <div v-if="loadingList" class="ev-muted">Loading…</div>
      <div v-else-if="!entries.length" class="ev-empty">
        No properties yet. Enter an address above to get your first estimate — it'll be saved here.
      </div>

      <div v-else class="ev-cards">
        <div v-for="e in entries" :key="e._k" class="ev-card" :class="{ open: openId === e.id }">
          <div class="ev-card-head" :class="{ clickable: hasVal(e) }" @click="hasVal(e) && toggle(e.id)">
            <div class="ev-card-main">
              <div class="ev-card-addr">{{ e.address }}</div>

              <template v-if="st(e) === 'estimating'">
                <div class="ev-card-est estimating">Estimating<span class="ev-dots"><i></i><i></i><i></i></span></div>
                <div class="ev-card-range">Getting your first estimate…</div>
              </template>

              <template v-else-if="st(e) === 'failed'">
                <div class="ev-card-est failed">Couldn't estimate</div>
                <div class="ev-card-range">{{ e.error || 'Try a more complete address' }} ·
                  <a class="ev-retry" @click.stop="reestimate(e)">Retry</a></div>
              </template>

              <template v-else>
                <div class="ev-card-est">{{ money(val(e).estimate_point, val(e)) }}</div>
                <div v-if="val(e).improvements_uplift > 0" class="ev-card-uplift">
                  <div>▲ +{{ money(val(e).improvements_uplift, val(e)) }} from your upgrades</div>
                  <div v-if="openId === e.id && upgrades(e).length" class="ev-uplift-items" @click.stop>
                    <div v-for="(f, i) in upgrades(e)" :key="i" class="ev-uplift-row">
                      <span class="ev-uplift-name">{{ f.label }}</span>
                      <span class="ev-uplift-amt">+{{ money(Number(String(f.effect).replace(/[^0-9]/g, '')), val(e)) }}</span>
                    </div>
                  </div>
                </div>
                <div class="ev-card-range">{{ money(val(e).estimate_min, val(e)) }}–{{ money(val(e).estimate_max, val(e)) }}</div>
              </template>
            </div>
            <div class="ev-card-side">
              <span v-if="st(e) === 'ready' && val(e).confidence === 'high'" class="ev-conf c-high">High confidence</span>
              <span v-else-if="st(e) === 'preliminary'" class="ev-conf c-refining">Refining…</span>
              <span v-else-if="st(e) === 'estimating'" class="ev-spinner"></span>
              <span v-if="hasVal(e)" class="ev-chev">{{ openId === e.id ? '▲' : '▼' }}</span>
              <button v-if="confirmDelId === e.id" class="ev-card-del confirm" :disabled="busyId === e.id" @click.stop="remove(e)">Remove?</button>
              <button v-else class="ev-card-del" title="Remove this property" @click.stop="askDelete(e.id)">✕</button>
            </div>
          </div>

          <!-- Expanded report — also renders during 'estimating' once inline Property details arrive -->
          <div v-if="openId === e.id && (hasVal(e) || (val(e) && val(e).subject))" class="ev-report">
            <!-- still computing the full comps report behind the fast headline -->
            <div v-if="st(e) === 'preliminary'" class="ev-banner refining">
              <span class="ev-spinner sm"></span> Preliminary estimate — refining with full comparable-sales analysis…
            </div>
            <!-- disclosure banners -->
            <div v-if="val(e).home_not_found" class="ev-banner warn">
              We couldn't confirm a home at this exact address — this estimate is based on nearby properties.
            </div>
            <div v-else-if="val(e).non_residential" class="ev-banner warn">
              This address appears to be non-residential (commercial or mixed-use) — a home valuation may not apply. Treat this estimate with caution.
            </div>
            <div v-if="val(e).comps_unverified" class="ev-banner info">
              In this market, comparables are listed/estimated prices rather than verified sale records.
            </div>

            <!-- SHARPEN — top of every report; prominent CTA + adaptive per-upgrade intake -->
            <div v-if="st(e) === 'ready'" class="ev-sharpen-top">
              <div v-if="sharpenFor !== e.id" class="ev-sharpen-cta" @click.stop="openSharpen(e)">
                <span class="ev-sharpen-ico">📈</span>
                <div class="ev-sharpen-cta-txt">
                  <b>Sharpen this estimate</b>
                  <span>Confirm your home's details and add upgrades — we ask what actually drives value for each one, so the number reflects your home, not a typical one.</span>
                </div>
                <span class="ev-sharpen-go">Add details →</span>
              </div>
              <div v-else class="ev-sharpen-form" @click.stop>
                <div class="ev-sub-h">Your home's details</div>
                <div class="ev-sharpen-row">
                  <input class="ev-input ev-sh-in" v-model="shBeds" type="tel" inputmode="numeric" placeholder="Beds" />
                  <input class="ev-input ev-sh-in" v-model="shBaths" type="tel" inputmode="numeric" placeholder="Baths" />
                  <input class="ev-input ev-sh-in" v-model="shSqft" type="tel" inputmode="numeric" placeholder="Sq ft" />
                  <input class="ev-input ev-sh-in" v-model="shYear" type="tel" inputmode="numeric" placeholder="Year built" />
                </div>
                <div v-if="shAssumed" class="ev-sharpen-hint">We estimated these from nearby homes — correct any that are off to sharpen your estimate.</div>
                <div class="ev-sub-h">Upgrades <span class="ev-sub-note">— describe each in your own words + the cost; we read it and estimate the value it adds</span></div>
                <div v-for="(u, i) in shUpgrades" :key="i" class="ev-upgrade">
                  <textarea class="ev-input ev-up-desc" v-model="u.desc" rows="2" placeholder="Describe the upgrade — what you did, brands, materials (e.g. “gutted the kitchen: quartz counters, custom cabinets, Sub-Zero fridge” or “new architectural-shingle roof”)"></textarea>
                  <div class="ev-sharpen-row">
                    <div class="ev-cost-field"><span class="ev-cost-sym">$</span><input class="ev-input ev-cost-in" v-model="u.cost" type="tel" inputmode="numeric" placeholder="Amount spent" /></div>
                    <input class="ev-input ev-sh-in" v-model="u.year" type="tel" inputmode="numeric" placeholder="Year" />
                    <button class="ev-mini ev-del ev-sh-x" title="Remove this upgrade" @click="shUpgrades.splice(i, 1)">✕</button>
                  </div>
                </div>
                <button class="ev-mini ev-sh-add" @click="addUpgrade()">+ Add another upgrade</button>
                <div class="ev-sharpen-note">Kiey reads each description + amount and estimates the resale value it adds. Your details save to this property when you recalculate — reopen "Sharpen" anytime to edit or remove them.</div>
                <div class="ev-sharpen-actions">
                  <button class="ev-mini ev-pdf" :disabled="busyId === e.id" @click="submitSharpen(e)">{{ busyId === e.id ? 'Saving…' : 'Save &amp; recalculate' }}</button>
                  <button class="ev-mini" @click="sharpenFor = null">Cancel</button>
                </div>
              </div>
            </div>

            <!-- 1 · YOUR HOME (labeled rows, matching the app's details card) -->
            <div class="ev-section">
              <div class="ev-section-h">Your home</div>
              <div v-if="subj(e)" class="ev-kv-card">
                <div class="ev-kv"><span class="k">Address</span><span class="v">{{ e.address }}</span></div>
                <div v-if="subj(e).last_sold_price" class="ev-kv"><span class="k">Purchased for</span><span class="v">{{ money(subj(e).last_sold_price, val(e)) }}<template v-if="subj(e).last_sold_year"> in {{ subj(e).last_sold_year }}</template></span></div>
                <div v-if="subj(e).beds" class="ev-kv"><span class="k">Bedrooms</span><span class="v">{{ subj(e).beds }}</span></div>
                <div v-if="subj(e).baths" class="ev-kv"><span class="k">Bathrooms</span><span class="v">{{ subj(e).baths }}</span></div>
                <div v-if="subj(e).sqft" class="ev-kv"><span class="k">Living area</span><span class="v">{{ num(subj(e).sqft) }} sq ft</span></div>
                <div v-if="subj(e).year_built" class="ev-kv"><span class="k">Year built</span><span class="v">{{ subj(e).year_built }}</span></div>
              </div>
              <div v-else class="ev-empty-note">Kiey is still gathering your home's details. Add your purchase below to complete them.</div>
            </div>

            <!-- 2 · YOUR HOME'S VALUE OVER TIME (right after Your home, like the app) -->
            <div v-if="appr(e).length > 1" class="ev-section">
              <div class="ev-section-h">Your home's value over time</div>
              <div v-if="subj(e) && subj(e).last_sold_price" class="ev-lastsold">Last sold for <b>{{ money(subj(e).last_sold_price, val(e)) }}</b><template v-if="subj(e).last_sold_year"> in {{ subj(e).last_sold_year }}</template></div>
              <svg v-if="openSpark" class="ev-line" :viewBox="'0 0 ' + openSpark.W + ' ' + openSpark.H" preserveAspectRatio="xMidYMid meet">
                <path :d="openSpark.fill" class="ev-line-fill" />
                <path :d="openSpark.line" class="ev-line-stroke" />
                <circle v-for="(p, i) in openSpark.pts" :key="'d' + i" :cx="p.x" :cy="p.y" :r="i === openSpark.pts.length - 1 ? 5 : 3" class="ev-line-dot" />
                <text v-for="(l, i) in openSpark.labels" :key="'l' + i" :x="l.x" :y="openSpark.labelY" :text-anchor="l.anchor" class="ev-line-lbl">{{ l.year }}</text>
              </svg>
            </div>
            <!-- No confirmed past sale (e.g. gated Canada data) — let the owner supply it to unlock the graph -->
            <div v-else-if="st(e) === 'ready'" class="ev-section">
              <div class="ev-section-h">Your home's value over time</div>
              <div v-if="anchorFor !== e.id" class="ev-empty-note">
                Kiey couldn't pull your purchase from public records here.
                <a class="ev-retry" @click.stop="openAnchor(e)">Add when you bought</a> and it'll chart what your home's done since.
              </div>
              <div v-else class="ev-anchor-form" @click.stop>
                <input class="ev-input ev-anchor-in" v-model="anchorYear" type="tel" inputmode="numeric" maxlength="4" placeholder="Year bought" />
                <input class="ev-input ev-anchor-in" v-model="anchorPrice" type="tel" inputmode="numeric" placeholder="Purchase price" />
                <button class="ev-mini ev-pdf" :disabled="busyId === e.id || !anchorValid" @click="submitAnchor(e)">
                  {{ busyId === e.id ? 'Saving…' : 'Chart it' }}
                </button>
                <button class="ev-mini" @click="anchorFor = null">Cancel</button>
              </div>
            </div>

            <!-- 3 · COMPARABLE SALES (side-by-side grid: Your home + each comp, "Your home ≈" row) -->
            <div v-if="comps(e).length" class="ev-section">
              <div class="ev-section-h">Comparable sales · 15 mi, last 12 mo</div>
              <div class="ev-grid-wrap">
                <div class="ev-grid-labels">
                  <div class="ev-gc name"></div>
                  <div class="ev-gc lbl">Price</div>
                  <div class="ev-gc lbl">Beds</div>
                  <div class="ev-gc lbl">Baths</div>
                  <div class="ev-gc lbl">Sq ft</div>
                  <div class="ev-gc lbl">$/sq ft</div>
                  <div class="ev-gc lbl">Year</div>
                  <div class="ev-gc lbl b">Your home ≈</div>
                </div>
                <div class="ev-grid-scroll">
                  <div class="ev-gcol yours">
                    <div class="ev-gc name b">Your home</div>
                    <div class="ev-gc">{{ money(val(e).estimate_point, val(e)) }}</div>
                    <div class="ev-gc">{{ hVal(hSpecs(e).beds, hSpecs(e).assumed) }}</div>
                    <div class="ev-gc">{{ hVal(hSpecs(e).baths, hSpecs(e).assumed) }}</div>
                    <div class="ev-gc">{{ hVal(hSpecs(e).sqft, hSpecs(e).assumed) }}</div>
                    <div class="ev-gc">{{ ppsf(val(e).estimate_point, hSpecs(e).sqft) }}</div>
                    <div class="ev-gc">{{ hSpecs(e).year_built || '—' }}</div>
                    <div class="ev-gc b">{{ money(val(e).estimate_point, val(e)) }}</div>
                  </div>
                  <div v-for="(c, i) in comps(e)" :key="i" class="ev-gcol">
                    <div class="ev-gc name b">{{ shortAddr(c.address) }}</div>
                    <div class="ev-gc">{{ money(c.price, val(e)) }}</div>
                    <div class="ev-gc">{{ c.beds || '—' }}<span v-if="winIdx(e, 'beds') === i" class="ev-win">✓</span></div>
                    <div class="ev-gc">{{ c.baths || '—' }}<span v-if="winIdx(e, 'baths') === i" class="ev-win">✓</span></div>
                    <div class="ev-gc">{{ c.sqft ? num(c.sqft) : '—' }}<span v-if="winIdx(e, 'sqft') === i" class="ev-win">✓</span></div>
                    <div class="ev-gc">{{ ppsf(c.price, c.sqft) }}<span v-if="winIdx(e, 'ppsf') === i" class="ev-win">✓</span></div>
                    <div class="ev-gc">{{ c.year_built || '—' }}</div>
                    <div class="ev-gc b" :class="c.adjusted ? (c.adjusted >= c.price ? 'up' : 'down') : ''">{{ c.adjusted ? money(c.adjusted, val(e)) : '—' }}</div>
                  </div>
                </div>
              </div>
              <div v-if="adjRange(e)" class="ev-takeaway"><template v-if="estInRange(e)">Adjusted for size, beds/baths and condition, {{ comps(e).length > 1 ? 'these comps point' : 'this comp points' }} to about {{ adjRange(e) }} for your home — which is where the {{ money(val(e).estimate_point, val(e)) }} estimate lands.</template><template v-else>Adjusted for size, beds/baths and condition, {{ comps(e).length > 1 ? 'these comps point' : 'this comp points' }} to about {{ adjRange(e) }} for your home. The {{ money(val(e).estimate_point, val(e)) }} estimate also weighs your last sale and the local market trend.</template></div>
              <div class="ev-grid-cap"><span class="ev-win">✓</span> marks the closest match to your home in each row. <template v-if="hSpecs(e).assumed">Your home's specs are estimated from nearby homes (public records didn't confirm them) — add details to sharpen. </template>"Your home ≈" is each comp's price adjusted to your home. Green = yours is worth more, red = less.</div>
            </div>

            <!-- 4 · COMPARABLE LISTINGS · ON MARKET NOW -->
            <div v-if="listings(e).length" class="ev-section">
              <div class="ev-section-h">Comparable listings · on market now</div>
              <div class="ev-grid-wrap">
                <div class="ev-grid-labels">
                  <div class="ev-gc name"></div>
                  <div class="ev-gc lbl">Asking</div>
                  <div class="ev-gc lbl">Beds</div>
                  <div class="ev-gc lbl">Baths</div>
                  <div class="ev-gc lbl">Sq ft</div>
                  <div class="ev-gc lbl b">$/sq ft</div>
                </div>
                <div class="ev-grid-scroll">
                  <div class="ev-gcol yours">
                    <div class="ev-gc name b">Your home</div>
                    <div class="ev-gc">{{ money(val(e).estimate_point, val(e)) }}</div>
                    <div class="ev-gc">{{ hVal(hSpecs(e).beds, hSpecs(e).assumed) }}</div>
                    <div class="ev-gc">{{ hVal(hSpecs(e).baths, hSpecs(e).assumed) }}</div>
                    <div class="ev-gc">{{ hVal(hSpecs(e).sqft, hSpecs(e).assumed) }}</div>
                    <div class="ev-gc b">{{ ppsf(val(e).estimate_point, hSpecs(e).sqft) }}</div>
                  </div>
                  <div v-for="(c, i) in listings(e)" :key="i" class="ev-gcol">
                    <div class="ev-gc name b">{{ shortAddr(c.address) }}</div>
                    <div class="ev-gc">{{ money(c.price, val(e)) }}</div>
                    <div class="ev-gc">{{ c.beds || '—' }}</div>
                    <div class="ev-gc">{{ c.baths || '—' }}</div>
                    <div class="ev-gc">{{ c.sqft ? num(c.sqft) : '—' }}</div>
                    <div class="ev-gc b">{{ ppsf(c.price, c.sqft) }}</div>
                  </div>
                </div>
              </div>
              <div v-if="listRange(e)" class="ev-takeaway">On the market now: {{ listRange(e).lo }}–{{ listRange(e).hi }}. Your home's estimate of {{ listRange(e).est }} sits {{ listRange(e).where }} what's currently listed nearby.</div>
              <div class="ev-grid-cap">Listings are asking prices, not sales — compare $/sq ft to spot homes priced above or below the market.</div>
            </div>

            <!-- 5 · HOW KIEY ARRIVED AT THIS PRICE (reasoning at the bottom, like the app) -->
            <div v-if="val(e).basis || (val(e).basis_points && val(e).basis_points.length)" class="ev-section">
              <div class="ev-section-h">How Kiey arrived at this price</div>
              <p v-if="val(e).basis" class="ev-basis">{{ val(e).basis }}</p>
              <ul v-if="val(e).basis_points && val(e).basis_points.length" class="ev-points">
                <li v-for="(p, i) in val(e).basis_points" :key="i">{{ p }}</li>
              </ul>
            </div>

            <!-- Applied upgrades — shows the owner-entered improvements that added resale value -->
            <div v-if="upgrades(e).length" class="ev-section">
              <div class="ev-section-h">Your upgrades</div>
              <div v-for="(f, i) in upgrades(e)" :key="i" class="ev-kv">
                <span class="k">{{ f.label }}</span><span class="v ev-up-add">+{{ money(Number(String(f.effect).replace(/[^0-9]/g, '')), val(e)) }}</span>
              </div>
            </div>

            <div class="ev-report-actions">
              <button class="ev-mini" :disabled="busyId === e.id" @click="reestimate(e)">
                {{ busyId === e.id ? 'Refreshing…' : 'Re-estimate' }}
              </button>
              <button class="ev-mini ev-del" :disabled="busyId === e.id" @click="remove(e)">Delete</button>
              <button v-if="st(e) === 'ready'" class="ev-mini ev-pdf" @click.stop="downloadPdf(e)">Download PDF</button>
              <span class="ev-stamp">Updated {{ stamp(e) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ACCURACY GATE — required details when we can't value the home from public records -->
    <div v-if="needModal" class="ev-need-overlay" @click.self="needModal = false">
      <div class="ev-need-card">
        <div class="ev-need-h">📋 We need a few details to value this home accurately</div>
        <p class="ev-need-why">{{ needReason }}</p>
        <div class="ev-need-addr">{{ needAddress }}</div>
        <div class="ev-need-grid">
          <label class="ev-need-f">Bedrooms<input v-model="ndBeds" type="tel" inputmode="numeric" class="ev-input" placeholder="e.g. 4" /></label>
          <label class="ev-need-f">Bathrooms<input v-model="ndBaths" type="tel" inputmode="decimal" class="ev-input" placeholder="e.g. 3" /></label>
          <label class="ev-need-f">Living area (sq ft)<input v-model="ndSqft" type="tel" inputmode="numeric" class="ev-input" placeholder="e.g. 2,400" /></label>
          <label class="ev-need-f">Year built<input v-model="ndYear" type="tel" inputmode="numeric" class="ev-input" placeholder="e.g. 1998" /></label>
          <label class="ev-need-f ev-need-lot">Lot size (acres)<input v-model="ndLot" type="tel" inputmode="decimal" class="ev-input" placeholder="e.g. 6.5" /><span class="ev-need-hint">Big driver for larger properties</span></label>
        </div>
        <!-- Adaptive, property-specific questions the model is unsure about -->
        <div v-if="ndQuestions.length" class="ev-need-adaptive">
          <div class="ev-need-sub">A few things that affect THIS home's value:</div>
          <label v-for="q in ndQuestions" :key="q.key" class="ev-need-q">
            {{ q.label }}
            <input v-model="ndAnswers[q.key]" type="text" class="ev-input" :placeholder="q.hint || ''" />
          </label>
        </div>
        <p v-if="needErr" class="ev-error">{{ needErr }}</p>
        <div class="ev-need-actions">
          <button class="ev-btn" :disabled="needBusy || !needReady" @click="submitNeedDetails">{{ needBusy ? 'Calculating…' : 'Calculate my estimate' }}</button>
          <button class="ev-link" @click="needModal = false">Cancel</button>
        </div>
        <p class="ev-need-note">Beds, baths and living area are required. The more accurate these are, the more accurate your estimate.</p>
      </div>
    </div>

    <!-- AI Q&A assistant — answers questions about the user's estimates AND explains how each was derived -->
    <div v-if="signedIn" class="ev-chat">
      <button v-if="!chatOpen" class="ev-chat-fab" @click="openChat">
        <span class="ev-chat-fab-ico">💬</span> Ask about your estimate
      </button>
      <div v-else class="ev-chat-panel">
        <div class="ev-chat-head">
          <span>Ask Kiey about your {{ entries.length === 1 ? 'home' : 'properties' }}</span>
          <button class="ev-chat-x" title="Close" @click="chatOpen = false">✕</button>
        </div>
        <div ref="chatScroll" class="ev-chat-body">
          <div v-if="!chatMessages.length" class="ev-chat-intro">
            Ask me anything about your estimates — how a value was calculated, what would add the most, or how two homes compare.
            <div class="ev-chat-chips">
              <button v-for="q in chatSuggestions" :key="q" class="ev-chat-chip" @click="sendChat(q)">{{ q }}</button>
            </div>
          </div>
          <div v-for="(m, i) in chatMessages" :key="i" class="ev-chat-msg" :class="m.role">{{ m.content }}</div>
          <div v-if="chatBusy" class="ev-chat-msg assistant ev-chat-typing"><span></span><span></span><span></span></div>
        </div>
        <div class="ev-chat-input">
          <input v-model="chatInput" class="ev-input ev-chat-in" placeholder="Ask a question…" :disabled="chatBusy" @keyup.enter="sendChat()" />
          <button class="ev-btn ev-chat-send" :disabled="chatBusy || !chatInput.trim()" @click="sendChat()">Send</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { firebase, firestore } from '@/config/firebase';
import initCaptcha, { getCaptchaInstance, clearCaptcha } from '@/config/captcha';
import config from '@/config/constants';
import axios from 'axios';

export default {
  name: 'EvaluationTool',
  filters: {
    cap(v) { return v ? v.charAt(0).toUpperCase() + v.slice(1) : v; },
  },
  data() {
    return {
      signedIn: false,
      // auth
      authStep: 'phone',
      phone: '',
      code: '',
      busy: false,
      authError: '',
      // dashboard
      newAddress: '',
      newCountry: '',
      addrSelected: false,
      submitting: false,
      valueError: '',
      entries: [],
      loadingList: false,
      openId: null,
      busyId: null,
      confirmDelId: null,  // card awaiting a delete confirmation
      brandOverride: {},   // saved letterhead (name/tag/accent) — persisted in localStorage
      brandEditing: false,
      brandForm: { name: '', tag: '', accent: '' },
      polls: {},
      anchorFor: null,   // id of the card whose "add your purchase" form is open
      anchorYear: '',
      anchorPrice: '',
      sharpenFor: null,  // id of the card whose "sharpen your estimate" form is open
      shBeds: '', shBaths: '', shSqft: '', shYear: '',
      shAssumed: false,  // true when the prefilled specs were estimated from nearby homes (prompt to confirm)
      shBaseline: null,  // prefilled spec values — to detect which the owner actually edited vs accepted
      // AI Q&A assistant
      chatOpen: false,
      chatMessages: [],   // [{ role: 'user'|'assistant', content }]
      chatInput: '',
      chatBusy: false,
      chatSuggestions: ['How did you calculate this value?', 'What would add the most value?', 'How can I make it more accurate?'],
      CURRENT_ENGINE_V: 5,   // cards below this are auto-re-estimated on load (see loadList)
      healedIds: new Set(),
      // "We need more info" accuracy gate
      needModal: false, needAddress: '', needCountry: '', needReason: '', needErr: '', needBusy: false,
      ndBeds: '', ndBaths: '', ndSqft: '', ndYear: '', ndLot: '',
      ndQuestions: [], ndAnswers: {},   // adaptive, property-specific questions + the owner's answers
      shUpgrades: [],
      upgradeTypes: ['Kitchen remodel', 'Bathroom remodel', 'Finished basement', 'Addition / extension',
        'New roof', 'New windows', 'New siding', 'HVAC / furnace / A/C', 'Pool', 'New deck', 'Garage',
        'New flooring', 'Landscaping', 'Other renovation'],
      // Adaptive intake — the value-driving questions PER upgrade type. Each option carries a quality
      // tier; the answers set how much of the cost is recouped (a luxury kitchen recoups more than a
      // builder-grade one) and are captured on the record so the estimate isn't cookie-cutter.
      upgradeSchema: {
        'Kitchen remodel': [
          { key: 'counters', label: 'Countertops', opts: [['Laminate', 'budget'], ['Granite', 'premium'], ['Quartz', 'premium'], ['Marble / Quartzite', 'luxury']] },
          { key: 'appliances', label: 'Appliances', opts: [['Builder-grade', 'budget'], ['Mid-range', 'standard'], ['High-end (Bosch, KitchenAid)', 'premium'], ['Luxury (Sub-Zero, Wolf, Thermador)', 'luxury']] },
          { key: 'cabinets', label: 'Cabinets', opts: [['Refaced', 'budget'], ['Stock', 'standard'], ['Semi-custom', 'premium'], ['Custom', 'luxury']] },
        ],
        'Bathroom remodel': [
          { key: 'scope', label: 'Scope', opts: [['Cosmetic refresh', 'standard'], ['Full gut / rebuild', 'premium']] },
          { key: 'finishes', label: 'Fixtures & tile', opts: [['Builder-grade', 'budget'], ['Mid-range', 'standard'], ['High-end', 'premium'], ['Luxury / spa', 'luxury']] },
        ],
        'New flooring': [
          { key: 'material', label: 'Material', opts: [['Carpet', 'budget'], ['Laminate', 'standard'], ['Luxury vinyl (LVP)', 'standard'], ['Hardwood', 'premium'], ['Tile / Stone', 'premium']] },
          { key: 'area', label: 'Area covered', opts: [['One room', 'standard'], ['Main level', 'standard'], ['Whole home', 'premium']] },
        ],
        'New roof': [{ key: 'material', label: 'Roofing material', opts: [['Asphalt (3-tab)', 'budget'], ['Architectural shingle', 'standard'], ['Metal', 'premium'], ['Tile / Slate', 'luxury']] }],
        'Addition / extension': [
          { key: 'added_sqft', label: 'Added square footage', ph: 'e.g. 400' },
          { key: 'kind', label: 'What was added', opts: [['Bedroom', 'standard'], ['Bathroom', 'premium'], ['Living space', 'standard'], ['Primary suite', 'premium'], ['In-law / ADU', 'premium']] },
        ],
        'New windows': [{ key: 'type', label: 'Window type', opts: [['Vinyl', 'standard'], ['Wood / clad', 'premium'], ['Energy-efficient / impact', 'premium']] }],
        'New siding': [{ key: 'material', label: 'Siding material', opts: [['Vinyl', 'standard'], ['Fiber cement (Hardie)', 'premium'], ['Brick / Stone', 'luxury']] }],
        'HVAC / furnace / A/C': [{ key: 'system', label: 'System', opts: [['Furnace only', 'standard'], ['A/C only', 'standard'], ['Heat pump', 'premium'], ['Mini-split', 'premium'], ['Full HVAC replacement', 'premium']] }],
        Pool: [{ key: 'type', label: 'Pool type', opts: [['Above-ground', 'budget'], ['In-ground', 'premium'], ['In-ground + spa', 'luxury']] }],
        'Finished basement': [{ key: 'finish', label: 'Finish level', opts: [['Basic', 'budget'], ['Standard', 'standard'], ['High-end (bath, wet bar)', 'premium'], ['Luxury (theater, suite)', 'luxury']] }],
        'New deck': [{ key: 'material', label: 'Decking material', opts: [['Pressure-treated wood', 'standard'], ['Composite (Trex)', 'premium'], ['Hardwood / stone', 'luxury']] }],
        Garage: [{ key: 'kind', label: 'Scope', opts: [['Door / opener', 'standard'], ['New detached', 'premium'], ['Attached addition', 'premium']] }],
        Landscaping: [{ key: 'scope', label: 'Scope', opts: [['Refresh', 'budget'], ['Full design / hardscape', 'premium'], ['Estate (pool deck, outdoor kitchen)', 'luxury']] }],
        'Other renovation': [
          { key: 'finish', label: 'Finish level', opts: [['Budget', 'budget'], ['Standard', 'standard'], ['Premium', 'premium'], ['Luxury', 'luxury']] },
          { key: 'desc', label: 'What was it?', ph: 'e.g. designer, make, model, materials' },
        ],
      },
      tmpSeq: 0,
    };
  },
  computed: {
    ext() { return config.phone_extension; },
    // White-label branding from query params — a licensee embeds ?brand=Acme%20Realty&tag=Home%20Values&accent=1E63FF
    // to fully rebrand the tool. Defaults to Kiey. Accent must be a 6-hex color (validated).
    brand() {
      let q = {};
      try { q = Object.fromEntries(new URLSearchParams(window.location.search)); } catch (e) { /* noop */ }
      // Saved letterhead (edited in-app, persisted) takes precedence, then ?brand= params, then default.
      const o = this.brandOverride || {};
      const name = o.name || q.brand || 'Kiey';
      const tag = (o.tag != null && o.tag !== '') ? o.tag : (q.tag || 'Home Value Estimator');
      const rawAccent = String(o.accent || q.accent || '').replace(/^#/, '');
      return {
        name: String(name).slice(0, 40),
        tag: String(tag).slice(0, 60),
        accent: /^[0-9a-fA-F]{6}$/.test(rawAccent) ? ('#' + rawAccent) : '#12b86c',
      };
    },
    anchorValid() {
      const y = parseInt(this.anchorYear, 10);
      const p = parseInt(String(this.anchorPrice).replace(/[^0-9]/g, ''), 10);
      return y > 1900 && y <= new Date().getFullYear() && p > 0;
    },
    // Line-graph geometry for the currently-open card (computed once, not per template reference).
    openSpark() {
      const e = this.entries.find((x) => x.id === this.openId);
      return e ? this.spark(e) : null;
    },
    // Required fields for the accuracy gate: beds, baths and living area.
    needReady() {
      const n = (x) => parseFloat(String(x).replace(/[^0-9.]/g, '')) > 0;
      return n(this.ndBeds) && n(this.ndBaths) && n(this.ndSqft);
    },
  },
  mounted() {
    this.loadBrand(); // saved letterhead (name/tagline/accent)
    // Resolve current auth state without forcing the tenant login flow.
    firebase.auth().onAuthStateChanged((user) => {
      this.signedIn = !!user;
      if (user) this.loadList();
    });
    this.$nextTick(() => { if (this.$refs.phoneInput) this.$refs.phoneInput.focus(); });
  },
  beforeDestroy() { this.clearPolls(); },
  methods: {
    // ---------- auth ----------
    async sendCode() {
      this.authError = '';
      this.busy = true;
      try {
        const clean = this.phone.replace(/[\s\-()]/g, '');
        const e164 = this.ext + clean;
        const expired = getCaptchaInstance() && getCaptchaInstance()._expired;
        let { auth, recaptcha } = initCaptcha(expired || false);
        try {
          window.confirmationResult = await auth.signInWithPhoneNumber(e164, recaptcha);
        } catch (err) {
          // reCAPTCHA hiccup — reset the verifier once and retry.
          console.warn('[eval sendCode] first attempt failed:', err && err.code, err && err.message);
          const r = initCaptcha(true);
          window.confirmationResult = await r.auth.signInWithPhoneNumber(e164, r.recaptcha);
        }
        this.authStep = 'code';
        this.$nextTick(() => { if (this.$refs.codeInput) this.$refs.codeInput.focus(); });
      } catch (err) {
        console.error('[eval sendCode] failed:', err && err.code, err && err.message);
        this.authError = this.friendly(err) || 'Could not send the code. Check the number and try again.';
      } finally {
        this.busy = false;
      }
    },
    async verifyCode() {
      this.authError = '';
      this.busy = true;
      try {
        if (!window.confirmationResult) throw new Error('Please request a new code.');
        const cred = firebase.auth.PhoneAuthProvider.credential(
          window.confirmationResult.verificationId, this.code.replace(/\D/g, ''));
        await firebase.auth().signInWithCredential(cred);
        // onAuthStateChanged flips signedIn + loads the list.
        this.code = '';
      } catch (err) {
        this.authError = 'That code didn\'t match. Please try again.';
      } finally {
        this.busy = false;
      }
    },
    async signOut() {
      try { await firebase.auth().signOut(); } catch (e) { /* noop */ }
      clearCaptcha();
      this.clearPolls();
      this.entries = [];
      this.openId = null;
      this.authStep = 'phone';
      this.phone = '';
    },
    friendly(err) {
      const c = err && err.code;
      if (c === 'auth/invalid-phone-number') return 'That phone number doesn\'t look right.';
      if (c === 'auth/too-many-requests') return 'Too many attempts. Please wait a bit and try again.';
      if (c === 'auth/invalid-app-credential' || c === 'auth/captcha-check-failed') {
        return 'We couldn\'t verify your device. Please refresh the page and try again.';
      }
      if (c === 'auth/quota-exceeded') return 'Verification is temporarily unavailable. Please try again shortly.';
      return '';
    },

    // ---------- API ----------
    async authHeaders() {
      const u = firebase.auth().currentUser;
      const token = u ? await u.getIdToken() : '';
      return { Authorization: 'Bearer ' + token };
    },
    // Normalize a server row so every field the UI mutates later is reactive from the start.
    normalize(d) {
      return { _k: d.id, id: d.id, address: d.address, status: d.status || (d.value && d.value.estimate_point ? 'ready' : 'estimating'),
        value: d.value || null, error: d.error || null, improvements: d.improvements || null, created_at: d.created_at || null, updated_at: d.updated_at || null };
    },
    async loadList() {
      this.loadingList = true;
      try {
        const headers = await this.authHeaders();
        const r = await axios.get(config.api_url + 'evaluation/list', { headers });
        const rows = (r.data && r.data.data && r.data.data.valuations) || [];
        this.entries = rows.map((d) => this.normalize(d));
        // Resume any estimate that was still running (e.g. page was reloaded mid-compute).
        this.entries.forEach((e) => { if (e.status === 'estimating' && e.id) this.watchValuation(e.id); });
        // AUTO-HEAL: silently re-estimate cards computed by an OLDER engine (before the consistency /
        // confidence / anchor fixes) so a user never keeps seeing a stale or mismatched number. The address
        // cache makes this fast + consistent, and the engine stamp means it runs at most once per card.
        this.entries.forEach((e) => {
          if (e.status === 'ready' && e.value && (e.value.engine_v || 0) < this.CURRENT_ENGINE_V && !this.healedIds.has(e.id)) {
            this.healedIds.add(e.id);
            this.silentReestimate(e);
          }
        });
      } catch (e) {
        this.entries = [];
      } finally {
        this.loadingList = false;
      }
    },
    // Google Places selection → capture the clean formatted address + country (country lets the CMA
    // pick US vs Canada sources/currency), then estimate immediately — same as the app's picker.
    onPlaceChanged(place) {
      if (!place) return;
      let country = '';
      if (place.address_components) {
        for (const comp of place.address_components) {
          if (comp.types && comp.types.includes('country')) country = comp.short_name || '';
        }
      }
      this.newAddress = place.formatted_address || place.name || this.newAddress;
      this.newCountry = country;
      this.addrSelected = true;   // a real geocoded address was chosen — now it can be estimated
      if (this.newAddress.trim().length >= 5) this.valueAddress();
    },
    // Keep the model synced while the user types (before selecting a suggestion); country is unknown
    // until a Places result is picked. Clear it + the selected flag so free-typed text (not a real
    // geocoded address) can't be submitted — the user must pick from the Google suggestions.
    onAddrInput(e) { this.newAddress = e.target.value; this.newCountry = ''; this.addrSelected = false; },
    async valueAddress() {
      const address = this.newAddress.trim();
      if (address.length < 5) return;
      const country = this.newCountry || undefined;
      this.valueError = '';
      // Don't save the same address twice — open the existing card instead of creating a duplicate.
      const norm = (a) => String(a || '').toLowerCase().replace(/[\s,]+/g, ' ').replace(/,?\s*usa$/i, '').trim();
      const dupe = this.entries.find((x) => x.id && !String(x.id).startsWith('pending-') && norm(x.address) === norm(address));
      if (dupe) {
        this.openId = dupe.id;
        this.valueError = 'That address is already saved below — opened it for you.';
        this.newAddress = ''; this.newCountry = ''; this.addrSelected = false;
        if (this.$refs.evAddr && this.$refs.evAddr.$el) this.$refs.evAddr.$el.value = '';
        return;
      }
      this.submitting = true;
      // Show the card instantly; the real estimate streams in.
      this.tmpSeq += 1;
      const tempK = 'pending-' + this.tmpSeq;
      const card = { _k: tempK, id: tempK, address, status: 'estimating', value: null, error: null, created_at: null, updated_at: null };
      this.entries.unshift(card);
      this.newAddress = '';
      this.newCountry = '';
      this.addrSelected = false;
      if (this.$refs.evAddr && this.$refs.evAddr.$el) this.$refs.evAddr.$el.value = '';
      try {
        const headers = await this.authHeaders();
        const r = await axios.post(config.api_url + 'evaluation/value', { address, country }, { headers });
        const data = r.data && r.data.data;
        if (data && data.needs_details) {
          // Can't value it accurately from records — collect the essentials + the property-specific
          // questions the model is unsure about, then estimate from the answers.
          this.removeByKey(tempK);
          this.openNeedModal(address, country, data.reason, data.prefill || {}, data.questions || []);
          return;
        }
        if (data && data.deduped && data.id) {
          // Server already had this address — drop the temp card and open the existing one.
          this.removeByKey(tempK);
          const ex = this.entries.find((x) => x.id === data.id);
          if (ex) { this.openId = data.id; if (this.st(ex) === 'estimating') this.watchValuation(data.id); }
          else { this.loadList(); this.openId = data.id; }
          this.valueError = 'That address is already saved below — opened it for you.';
          return;
        }
        if (data && data.id) {
          card.id = data.id; // swap temp -> real id (key stays tempK, no remount)
          // Paint Property details immediately from the inline response (like the app), then expand.
          if (data.subject) card.value = { subject: data.subject };
          this.openId = data.id;
          this.watchValuation(data.id);
        } else {
          this.removeByKey(tempK);
          this.valueError = 'Could not start that estimate.';
        }
      } catch (e) {
        this.removeByKey(tempK);
        const msg = e && e.response && e.response.data && e.response.data.error;
        this.valueError = msg || 'Could not start that estimate. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
    async reestimate(e) {
      if (!e.id || String(e.id).startsWith('pending-')) return;
      e.status = 'estimating';
      e.error = null;
      this.busyId = e.id;
      try {
        const headers = await this.authHeaders();
        await axios.post(config.api_url + 'evaluation/reestimate', { id: e.id }, { headers });
        this.watchValuation(e.id);
      } catch (err) {
        e.status = (e.value && e.value.estimate_point) ? 'ready' : 'failed';
      } finally {
        this.busyId = null;
      }
    },
    // ---------- accuracy gate: "we need more info" ----------
    openNeedModal(address, country, reason, prefill, questions) {
      this.needAddress = address; this.needCountry = country || '';
      this.needReason = reason || "We couldn't find records for this home — answer a few questions so the estimate reflects your home, not a typical one.";
      this.ndBeds = ''; this.ndBaths = ''; this.ndSqft = '';
      this.ndYear = (prefill && prefill.year_built) ? String(prefill.year_built) : '';
      this.ndLot = ''; this.needErr = '';
      this.ndQuestions = Array.isArray(questions) ? questions : [];
      this.ndAnswers = {};
      this.ndQuestions.forEach((q) => { this.$set(this.ndAnswers, q.key, ''); });
      this.needModal = true;
    },
    async submitNeedDetails() {
      if (!this.needReady || this.needBusy) return;
      const posN = (x) => { const v = parseFloat(String(x).replace(/[^0-9.]/g, '')); return v > 0 ? v : null; };
      const details = { beds: posN(this.ndBeds), baths: posN(this.ndBaths), sqft: Math.round(posN(this.ndSqft)) };
      if (posN(this.ndYear) && posN(this.ndYear) > 1800) details.year_built = Math.round(posN(this.ndYear));
      if (posN(this.ndLot)) details.lot_acres = posN(this.ndLot);
      // The owner's answers to the adaptive, property-specific questions.
      const intake = this.ndQuestions
        .map((q) => ({ label: q.label, answer: String(this.ndAnswers[q.key] || '').trim() }))
        .filter((x) => x.answer);
      this.needBusy = true; this.needErr = '';
      try {
        const headers = await this.authHeaders();
        const r = await axios.post(config.api_url + 'evaluation/value', { address: this.needAddress, country: this.needCountry || undefined, details, intake }, { headers });
        const data = r.data && r.data.data;
        if (data && data.id) {
          this.tmpSeq += 1;
          const tempK = 'pending-' + this.tmpSeq;
          const card = { _k: tempK, id: data.id, address: this.needAddress, status: 'estimating', value: data.subject ? { subject: data.subject } : null, error: null, created_at: null, updated_at: null };
          this.entries.unshift(card);
          this.openId = data.id;
          this.watchValuation(data.id);
          this.needModal = false;
        } else {
          this.needErr = 'Could not start that estimate. Please try again.';
        }
      } catch (e) {
        this.needErr = (e && e.response && e.response.data && e.response.data.error) || 'Could not start that estimate. Please try again.';
      } finally { this.needBusy = false; }
    },
    openAnchor(e) { this.anchorFor = e.id; this.anchorYear = ''; this.anchorPrice = ''; },
    // Improvements the owner already entered (from value.factors) — with their +$ resale effect.
    upgrades(e) { return (this.val(e).factors || []).filter((f) => f && f.is_improvement); },
    // ---------- AI Q&A assistant ----------
    openChat() { this.chatOpen = true; this.$nextTick(() => this.scrollChat()); },
    scrollChat() { const el = this.$refs.chatScroll; if (el) el.scrollTop = el.scrollHeight; },
    // After a reply arrives, align the START of the answer near the top so it reads top-to-bottom,
    // instead of jumping to the bottom of a long message.
    scrollToAnswer() {
      const el = this.$refs.chatScroll;
      if (!el) return;
      const answers = el.querySelectorAll('.ev-chat-msg.assistant:not(.ev-chat-typing)');
      const last = answers[answers.length - 1];
      el.scrollTop = last ? Math.max(0, last.offsetTop - 12) : el.scrollHeight;
    },
    // Silently re-run a stale estimate (computed by an older engine) — refresh in the background.
    async silentReestimate(e) {
      try {
        const headers = await this.authHeaders();
        e.status = 'estimating';
        await axios.post(config.api_url + 'evaluation/reestimate', { id: e.id }, { headers });
        this.watchValuation(e.id);
      } catch (err) { e.status = 'ready'; }
    },
    async sendChat(preset) {
      const text = String(preset || this.chatInput || '').trim();
      if (!text || this.chatBusy) return;
      this.chatMessages.push({ role: 'user', content: text });
      this.chatInput = '';
      this.chatBusy = true;
      this.$nextTick(() => this.scrollChat());
      try {
        const headers = await this.authHeaders();
        // focus_id = the currently-open card, so "this home" means the one they're looking at.
        const r = await axios.post(config.api_url + 'evaluation/chat',
          { messages: this.chatMessages, focus_id: this.openId || null }, { headers });
        const reply = (r.data && r.data.data && r.data.data.reply) || "Sorry — I couldn't answer that.";
        this.chatMessages.push({ role: 'assistant', content: reply });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || "Sorry — I couldn't answer that right now.";
        this.chatMessages.push({ role: 'assistant', content: msg });
      } finally {
        this.chatBusy = false;
        this.$nextTick(() => this.scrollToAnswer());
      }
    },
    openSharpen(e) {
      this.sharpenFor = e.id;
      const s = this.hSpecs(e);
      // Pre-fill with the best specs we have so the owner CONFIRMS/CORRECTS instead of retyping from
      // scratch: real property-record specs first, else the assumed profile (median of nearby homes).
      // shAssumed flags that some were guessed from comps, so we prompt the owner to double-check them.
      const sub = this.subj(e) || {};
      this.shBeds = sub.beds || s.beds || '';
      this.shBaths = sub.baths || s.baths || '';
      this.shSqft = sub.sqft || s.sqft || '';
      this.shYear = sub.year_built || s.year_built || '';
      this.shAssumed = !!s.assumed && !(sub.beds || sub.baths || sub.sqft);
      // Remember what we prefilled so submit can tell which fields the owner actually edited vs just
      // accepted — accepting a guessed spec must NOT be treated as "confirmed".
      this.shBaseline = { beds: String(this.shBeds || ''), baths: String(this.shBaths || ''), sqft: String(this.shSqft || ''), year: String(this.shYear || '') };
      // Preload previously-saved upgrades so the owner can edit or remove them (not re-type from scratch).
      const saved = Array.isArray(e.improvements) ? e.improvements : [];
      this.shUpgrades = saved.length
        ? saved.map((u) => ({ desc: u.desc || u.label || '', cost: u.cost || '', year: u.year || '' }))
        : [{ desc: '', cost: '', year: '' }];
    },
    addUpgrade() { this.shUpgrades.push({ desc: '', cost: '', year: '' }); },
    // Recalculate with owner-confirmed specs + free-text upgrades → specs tighten the comps baseline;
    // each upgrade's description + cost is valued by the AI pass server-side.
    async submitSharpen(e) {
      const posN = (x) => { const v = parseFloat(String(x).replace(/[^0-9.]/g, '')); return v > 0 ? v : null; };
      // Only send a spec as CONFIRMED if the owner actually entered/changed it. If specs were assumed and
      // they left a field at the prefilled guess, don't confirm it — otherwise a guessed number would
      // flip the estimate to the deterministic path and a false "high confidence". (Real, unassumed
      // prefills — from public records — are already confirmed and always pass through.)
      const bl = this.shBaseline || {};
      const confirmed = (val, key) => {
        const v = posN(val);
        if (!v) return null;
        if (this.shAssumed && String(val) === bl[key]) return null;
        return v;
      };
      const details = {};
      const cB = confirmed(this.shBeds, 'beds'); if (cB) details.beds = cB;
      const cBa = confirmed(this.shBaths, 'baths'); if (cBa) details.baths = cBa;
      const cS = confirmed(this.shSqft, 'sqft'); if (cS) details.sqft = Math.round(cS);
      const cY = confirmed(this.shYear, 'year'); if (cY && cY > 1800) details.year_built = Math.round(cY);
      const improvements = this.shUpgrades
        .filter((u) => u.desc && u.desc.trim() && posN(u.cost))
        .map((u) => ({ desc: u.desc.trim().slice(0, 200), cost: Math.round(posN(u.cost)), year: (parseInt(u.year, 10) || null) }));
      this.busyId = e.id;
      e.error = null;
      try {
        const headers = await this.authHeaders();
        await axios.post(config.api_url + 'evaluation/reestimate', { id: e.id, details, improvements }, { headers });
        e.status = 'estimating';
        this.sharpenFor = null;
        this.watchValuation(e.id);
      } catch (err) {
        this.valueError = 'Could not recalculate — please try again.';
      } finally { this.busyId = null; }
    },
    // Owner supplies their purchase (year + price) → re-estimate with that anchor so the CMA can chart
    // value over time even where public sale data is gated (Canada / TRREB).
    async submitAnchor(e) {
      const year = parseInt(this.anchorYear, 10);
      const price = parseInt(String(this.anchorPrice).replace(/[^0-9]/g, ''), 10);
      if (!(year > 1900) || !(price > 0)) return;
      this.busyId = e.id;
      e.error = null;
      try {
        const headers = await this.authHeaders();
        await axios.post(config.api_url + 'evaluation/reestimate', { id: e.id, anchor: { year, price } }, { headers });
        e.status = 'estimating';
        this.anchorFor = null;
        this.watchValuation(e.id);
      } catch (err) {
        this.valueError = 'Could not save that purchase — please try again.';
      } finally {
        this.busyId = null;
      }
    },
    loadBrand() { try { this.brandOverride = JSON.parse(localStorage.getItem('ev_brand') || '{}') || {}; } catch (e) { this.brandOverride = {}; } },
    openBrandEdit() {
      this.brandForm = {
        name: this.brandOverride.name || '',
        tag: this.brandOverride.tag != null ? this.brandOverride.tag : '',
        accent: String(this.brandOverride.accent || '').replace(/^#/, ''),
      };
      this.brandEditing = true;
    },
    saveBrand() {
      const o = {
        name: String(this.brandForm.name || '').slice(0, 40),
        tag: String(this.brandForm.tag || '').slice(0, 60),
        accent: String(this.brandForm.accent || '').replace(/[^0-9a-fA-F]/g, '').slice(0, 6),
      };
      this.brandOverride = o;
      try { localStorage.setItem('ev_brand', JSON.stringify(o)); } catch (e) { /* noop */ }
      this.brandEditing = false;
    },
    resetBrand() {
      this.brandOverride = {};
      try { localStorage.removeItem('ev_brand'); } catch (e) { /* noop */ }
      this.brandEditing = false;
    },
    // First tap arms the confirm (✕ → "Remove?"); auto-disarms after 4s so a stray tap can't delete.
    askDelete(id) {
      this.confirmDelId = id;
      clearTimeout(this._delTimer);
      this._delTimer = setTimeout(() => { if (this.confirmDelId === id) this.confirmDelId = null; }, 4000);
    },
    async remove(e) {
      this.busyId = e.id;
      try {
        const headers = await this.authHeaders();
        await axios.post(config.api_url + 'evaluation/delete', { id: e.id }, { headers });
        this.stopPoll(e.id);
        this.entries = this.entries.filter((x) => x.id !== e.id);
        if (this.openId === e.id) this.openId = null;
      } catch (err) { this.valueError = 'Could not remove that property — please try again.'; } finally { this.busyId = null; this.confirmDelId = null; }
    },

    // Real-time result via a Firestore listener (same as the app's profile listener) — updates the
    // instant the trigger writes preliminary/ready, with zero polling latency. Falls back to polling on
    // any error (e.g. the read rule not yet deployed, or an offline client).
    watchValuation(id) {
      this.stopPoll(id);
      const u = firebase.auth().currentUser;
      if (!u) { this.pollValuation(id); return; }
      const started = Date.now();
      let done = false;
      const unsub = firestore.collection('evaluation_users').doc(u.uid).collection('valuations').doc(id)
        .onSnapshot((snap) => {
          const d = snap.data();
          if (!d) return;
          this.mergeEntry(id, d);
          if (d.status === 'ready' || d.status === 'failed') { done = true; this.stopPoll(id); }
        }, () => { this.stopPoll(id); this.pollValuation(id); });
      this.polls[id] = unsub;
      setTimeout(() => {
        if (!done && this.polls[id] && Date.now() - started > 149000) {
          this.mergeEntry(id, { status: 'failed', error: 'Taking longer than expected — tap Retry' });
          this.stopPoll(id);
        }
      }, 150000);
    },
    // ---------- polling (fallback when the listener can't read the doc) ----------
    pollValuation(id) {
      this.stopPoll(id);
      const started = Date.now();
      const tick = async () => {
        try {
          const headers = await this.authHeaders();
          const r = await axios.get(config.api_url + 'evaluation/get', { params: { id }, headers });
          const d = r.data && r.data.data;
          if (d && (d.status === 'ready' || d.status === 'failed')) {
            this.mergeEntry(id, d);
            this.stopPoll(id);
            return;
          }
          // Fast first-pass number: render it now, but keep polling for the full comps report.
          if (d && d.status === 'preliminary') this.mergeEntry(id, d);
        } catch (e) { /* transient — keep polling */ }
        const elapsed = Date.now() - started;
        if (elapsed < 150000) {
          // Poll fast early so the ~2-5s preliminary number pops immediately; ease off once it's up.
          this.polls[id] = setTimeout(tick, elapsed < 30000 ? 1500 : 4000);
        } else {
          this.mergeEntry(id, { status: 'failed', error: 'Taking longer than expected — tap Retry' });
          this.stopPoll(id);
        }
      };
      this.polls[id] = setTimeout(tick, 1200);   // first check quickly
    },
    mergeEntry(id, d) {
      const e = this.entries.find((x) => x.id === id);
      if (!e) return;
      if (d.value) e.value = d.value;   // only set a real value — never wipe the inline Property details with null
      if (d.improvements !== undefined) e.improvements = d.improvements; // saved upgrades → editable on reopen
      if (d.status !== undefined) e.status = d.status;
      if (d.error !== undefined) e.error = d.error;
      if (d.updated_at) e.updated_at = d.updated_at;
      if ((e.status === 'ready' || e.status === 'preliminary') && this.openId == null) this.openId = id; // auto-open the first result
    },
    removeByKey(k) { this.entries = this.entries.filter((x) => x._k !== k); },
    // this.polls[id] holds either a setTimeout id (polling fallback) or a Firestore unsubscribe fn (listener).
    stopPoll(id) { const h = this.polls[id]; if (h) { if (typeof h === 'function') h(); else clearTimeout(h); delete this.polls[id]; } },
    clearPolls() { Object.values(this.polls).forEach((h) => { if (typeof h === 'function') h(); else clearTimeout(h); }); this.polls = {}; },

    // ---------- view helpers ----------
    st(e) { return e.status || ((e.value && e.value.estimate_point) ? 'ready' : 'estimating'); },
    hasVal(e) { const s = this.st(e); return s === 'ready' || s === 'preliminary'; },
    toggle(id) { this.openId = this.openId === id ? null : id; },
    val(e) { return e.value || {}; },
    comps(e) { return (this.val(e).comps || []).slice(0, 3); },
    listings(e) { return (this.val(e).listings || []).slice(0, 3); },
    subj(e) { const s = this.val(e).subject; return (s && (s.beds || s.baths || s.sqft || s.year_built || s.last_sold_price)) ? s : null; },
    appr(e) { return (this.val(e).appreciation || []).filter((a) => a && a.value > 0); },
    shortAddr(a) { return String(a || '').split(',')[0]; },
    // Your home's specs for the comparison grid — real specs, or the assumed profile (median of nearby
    // homes) parsed out when the address has no confirmed specs. `assumed` flags the ~ prefix.
    hSpecs(e) {
      const s = this.subj(e) || {};
      let beds = s.beds || null; let baths = s.baths || null; let sqft = s.sqft || null;
      let assumed = false;
      const prof = this.val(e).assumed_profile;
      if (prof && (!beds || !baths || !sqft)) {
        const mb = prof.match(/([\d.]+)\s*bed/i); const mba = prof.match(/([\d.]+)\s*bath/i); const msq = prof.match(/([\d,]+)\s*sq/i);
        if (!beds && mb) { beds = parseFloat(mb[1]); assumed = true; }
        if (!baths && mba) { baths = parseFloat(mba[1]); assumed = true; }
        if (!sqft && msq) { sqft = parseInt(msq[1].replace(/,/g, ''), 10); assumed = true; }
      }
      return { beds, baths, sqft, year_built: s.year_built || null, assumed };
    },
    hVal(v, assumed) { return v ? (assumed ? '~' : '') + this.num(v) : '—'; },
    // Price per square foot — the real "is it priced high?" signal.
    ppsf(price, sqft) { return (price > 0 && sqft > 0) ? `$${Math.round(price / sqft).toLocaleString('en-US')}` : '—'; },
    // Is the headline within the range the adjusted comps support? (After the backend clamp it should be —
    // but fallback values don't pass through the comp engine, so the copy still guards for it.)
    estInRange(e) {
      const adj = this.comps(e).map((c) => c.adjusted).filter((n) => n > 0);
      if (adj.length < 2) return true;
      const p = this.val(e).estimate_point || 0;
      return p >= Math.min(...adj) * 0.99 && p <= Math.max(...adj) * 1.01;
    },
    // Which comp best matches your home in a given category (closest value) — the "winner" gets a ✓.
    winIdx(e, key) {
      const hs = this.hSpecs(e);
      const target = key === 'ppsf' ? (hs.sqft ? this.val(e).estimate_point / hs.sqft : null) : hs[key];
      if (!target) return -1;
      let best = -1; let bd = Infinity;
      this.comps(e).forEach((c, i) => {
        const v = key === 'ppsf' ? (c.sqft ? c.price / c.sqft : null) : c[key];
        if (!(v > 0)) return;
        const d = Math.abs(v - target);
        if (d < bd) { bd = d; best = i; }
      });
      return best;
    },
    // Listings takeaway: asking range + where this home's estimate sits against what's on the market.
    listRange(e) {
      const ps = this.listings(e).map((c) => c.price).filter((n) => n > 0).sort((a, b) => a - b);
      if (!ps.length) return null;
      const est = this.val(e).estimate_point || 0;
      const lo = ps[0]; const hi = ps[ps.length - 1];
      let where = 'right in line with';
      if (est && est < lo * 0.97) where = 'below';
      else if (est && est > hi * 1.03) where = 'above';
      return { lo: this.money(lo, this.val(e)), hi: this.money(hi, this.val(e)), where, est: this.money(est, this.val(e)) };
    },
    // Value-over-time as a line graph identical to the app's LineGraphView: soft area fill + 2.5px
    // accent line + a dot on every point + a year label under each (thinned when >8 points).
    spark(e) {
      const s = this.appr(e);
      if (s.length < 2) return null;
      const W = 600; const H = 150; const padX = 10; const padTop = 14; const padBottom = 32;
      const w = W - padX * 2; const h = H - padTop - padBottom;
      const vals = s.map((a) => a.value);
      const minV = Math.min(...vals); const maxV = Math.max(...vals);
      const span = Math.max(maxV - minV, 1);
      const pts = s.map((a, i) => ({
        x: padX + (i / (s.length - 1)) * w,
        y: padTop + (1 - (a.value - minV) / span) * h,
        year: a.year,
      }));
      const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
      const fill = `M${padX} ${(padTop + h).toFixed(1)} ${pts.map((p) => `L${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')} L${(padX + w).toFixed(1)} ${(padTop + h).toFixed(1)} Z`;
      const step = s.length > 8 ? 2 : 1;
      const last = pts.length - 1;
      const labels = [];
      pts.forEach((p, i) => {
        // Always show the last year; show a stepped year ONLY if it's far enough from the last that the
        // two labels won't collide (a step label right next to the forced last would overlap it).
        const include = (i === last) || (i % step === 0 && (last - i) >= step);
        if (!include) return;
        // Keep the end labels in-bounds (start-anchor the first, end-anchor the last), like the app.
        const anchor = i === 0 ? 'start' : (i === last ? 'end' : 'middle');
        const x = i === 0 ? padX : (i === last ? W - padX : p.x);
        labels.push({ x, year: p.year, anchor });
      });
      return {
        W, H, line, fill, pts, labels, labelY: H - 8,
      };
    },
    // The range the "Your home ≈" column points to — what the adjusted comps imply for this home.
    adjRange(e) {
      const adj = this.comps(e).map((c) => c.adjusted).filter((n) => n > 0).sort((a, b) => a - b);
      if (!adj.length) return '';
      const lo = adj[0]; const hi = adj[adj.length - 1];
      return lo === hi ? this.money(lo, this.val(e)) : `${this.money(lo, this.val(e))} – ${this.money(hi, this.val(e))}`;
    },
    barH(a, e) {
      const list = this.appr(e);
      const max = Math.max.apply(null, list.map((x) => x.value));
      const min = Math.min.apply(null, list.map((x) => x.value));
      if (max === min) return 60;
      return 20 + Math.round(((a.value - min) / (max - min)) * 75);
    },
    money(n, v) {
      if (n == null || isNaN(n)) return '—';
      const sym = (v && v.currency_symbol) || '$';
      if (n >= 1000000) return sym + (n / 1000000).toFixed(2).replace(/\.00$/, '') + 'M';
      if (n >= 1000) return sym + Math.round(n / 1000) + 'K';
      return sym + Math.round(n);
    },
    num(n) { return n == null ? '' : Number(n).toLocaleString(); },
    stamp(e) {
      const s = e.updated_at && (e.updated_at._seconds || e.updated_at.seconds);
      if (!s) return 'just now';
      const d = new Date(s * 1000);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    },
    // Branded, self-contained PDF report — opens a print-optimized window (Save as PDF). Carries the
    // white-label brand/tagline/accent, so a licensee's report is THEIR brand, not Kiey's.
    downloadPdf(e) {
      const v = this.val(e); const b = this.brand; const m = (n) => this.money(n, v); const num = this.num;
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      const meta = (c) => [c.beds ? c.beds + ' bd' : '', c.baths ? c.baths + ' ba' : '', c.sqft ? num(c.sqft) + ' sqft' : '', c.date || '', c.distance_mi != null ? c.distance_mi + ' mi' : ''].filter(Boolean).join(' · ');
      const rows = (arr, adj) => (arr || []).map((c) => `<tr><td>${esc(c.address)}</td><td class="dim">${esc(meta(c))}</td><td class="r"><b>${m(c.price)}</b>${adj && c.adjusted ? `<div class="adj">Adj. ${m(c.adjusted)}</div>` : ''}</td></tr>`).join('');
      const s = this.subj(e);
      const subjParts = s ? [s.beds ? s.beds + ' bd' : '', s.baths ? s.baths + ' ba' : '', s.sqft ? num(s.sqft) + ' sqft' : '', s.year_built ? 'Built ' + s.year_built : '', s.last_sold_price ? 'Last sold ' + m(s.last_sold_price) + (s.last_sold_year ? ' (' + s.last_sold_year + ')' : '') : ''].filter(Boolean) : [];
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(b.name)} — Home Value Report</title><style>
        *{box-sizing:border-box}body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1b2740;max-width:720px;margin:0 auto;padding:32px}
        .head{display:flex;align-items:baseline;gap:10px;border-bottom:3px solid ${b.accent};padding-bottom:12px;margin-bottom:20px}
        .brand{font-size:22px;font-weight:800;color:${b.accent}}.tag{font-size:13px;color:#8593ad;font-weight:600}
        h1{font-size:18px;margin:0 0 4px}.addr{color:#5a6783;font-size:14px;margin-bottom:16px}
        .val{font-size:40px;font-weight:800;color:${b.accent};line-height:1}.range{color:#5a6783;margin:6px 0 22px}
        h2{font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#8593ad;margin:22px 0 8px;border-top:1px solid #eef1f7;padding-top:14px}
        table{width:100%;border-collapse:collapse;font-size:13px}td{padding:7px 0;border-bottom:1px solid #f2f4f9;vertical-align:top}
        td.r{text-align:right;white-space:nowrap}.dim{color:#8593ad}.adj{color:${b.accent};font-weight:700;font-size:11px}
        .chips span{display:inline-block;background:#f2f4f9;border-radius:99px;padding:3px 10px;margin:0 6px 6px 0;font-size:12px;font-weight:600}
        ul{margin:6px 0;padding-left:18px}li{font-size:13px;margin-bottom:4px;color:#475067}p{font-size:13px;color:#475067;line-height:1.5}
        .foot{margin-top:28px;padding-top:12px;border-top:1px solid #eef1f7;color:#9aa6bd;font-size:11px;line-height:1.5}
      </style></head><body>
        <div class="head"><div class="brand">${esc(b.name)}</div><div class="tag">${esc(b.tag)}</div></div>
        <h1>Home Value Report</h1><div class="addr">${esc(e.address)}</div>
        <div class="val">${m(v.estimate_point)}</div><div class="range">${m(v.estimate_min)} – ${m(v.estimate_max)}${v.confidence === 'high' ? ' · high confidence' : ''}</div>
        ${subjParts.length ? `<h2>Property details</h2><div class="chips"><span>${subjParts.map(esc).join('</span><span>')}</span></div>` : ''}
        ${(v.comps && v.comps.length) ? `<h2>Comparable sales</h2><table>${rows(v.comps, true)}</table>` : ''}
        ${(v.listings && v.listings.length) ? `<h2>On the market now</h2><table>${rows(v.listings, false)}</table>` : ''}
        ${(v.basis || (v.basis_points && v.basis_points.length)) ? `<h2>How this was calculated</h2>${v.basis ? `<p>${esc(v.basis)}</p>` : ''}${(v.basis_points && v.basis_points.length) ? `<ul>${v.basis_points.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}` : ''}
        <div class="foot">Report generated by ${esc(b.name)} on ${new Date().toLocaleDateString()}. This is an automated estimate based on comparable sales and public data — it is not a formal appraisal.</div>
        <scr` + `ipt>window.onload=function(){window.focus();window.print();}</scr` + `ipt>
      </body></html>`;
      const w = window.open('', '_blank');
      if (!w) { this.valueError = 'Enable pop-ups to download the PDF report.'; return; }
      w.document.open(); w.document.write(html); w.document.close();
    },
  },
};
</script>

<style scoped>
.ev-root { min-height: 100vh; background: #f4f6fa; color: #061D47; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.ev-top { background: #061D47; }
.ev-top-inner { max-width: 760px; margin: 0 auto; display: flex; align-items: center; gap: 12px; padding: 14px 18px; }
.ev-brand { color: #fff; font-weight: 800; font-size: 20px; text-decoration: none; letter-spacing: -0.5px; }
.ev-brand-tag { color: #9fb3d8; font-size: 13px; font-weight: 600; }
.ev-signout { margin-left: auto; background: transparent; border: 1px solid #2c4474; color: #cdd9ef; padding: 6px 12px; border-radius: 8px; font-size: 13px; cursor: pointer; }
.ev-signout:hover { background: #0d2a5c; }

/* auth */
.ev-auth { display: flex; justify-content: center; padding: 48px 18px; }
.ev-auth-card { background: #fff; border-radius: 18px; box-shadow: 0 10px 40px rgba(6,29,71,0.10); padding: 34px 30px; max-width: 440px; width: 100%; }
.ev-h1 { font-size: 30px; font-weight: 800; margin: 0 0 10px; letter-spacing: -0.8px; }
.ev-sub { color: #5a6783; font-size: 15px; line-height: 1.5; margin: 0 0 24px; }
.ev-form { display: flex; flex-direction: column; gap: 12px; }
.ev-label { font-size: 13px; font-weight: 600; color: #475067; }
.ev-phone-row { display: flex; align-items: center; gap: 8px; }
.ev-ext { font-weight: 700; color: #061D47; font-size: 17px; }
.ev-input { flex: 1; width: 100%; box-sizing: border-box; border: 1.5px solid #d6deeb; border-radius: 12px; padding: 14px 14px; font-size: 16px; color: #061D47; outline: none; background: #fbfcfe; }
.ev-input:focus { border-color: var(--ev-accent, #12b86c); background: #fff; }
.ev-code { letter-spacing: 8px; text-align: center; font-size: 22px; font-weight: 700; }
.ev-btn { background: var(--ev-accent, #12b86c); color: #fff; border: none; border-radius: 12px; padding: 14px; font-size: 16px; font-weight: 700; cursor: pointer; transition: opacity .15s; }
.ev-btn:disabled { opacity: 0.45; cursor: default; }
.ev-btn:not(:disabled):hover { background: #0fa760; }
.ev-link { background: none; border: none; color: #5a6783; font-size: 13px; cursor: pointer; padding: 4px; }
.ev-error { color: #d23b3b; font-size: 14px; margin: 12px 0 0; }

/* dashboard */
.ev-dash { max-width: 760px; margin: 0 auto; padding: 28px 18px 60px; }
.ev-new { background: #fff; border-radius: 16px; box-shadow: 0 6px 24px rgba(6,29,71,0.07); padding: 22px; margin-bottom: 28px; }
.ev-h2 { font-size: 19px; font-weight: 800; margin: 0 0 14px; letter-spacing: -0.4px; }
.ev-new-row { display: flex; gap: 10px; }
.ev-btn-go { white-space: nowrap; padding: 14px 18px; }
.ev-hint { color: #8593ad; font-size: 13px; margin: 12px 0 0; }
.ev-saved-h { display: flex; align-items: center; gap: 10px; }
.ev-count { background: #e3eaf6; color: #2c4474; font-size: 13px; font-weight: 700; border-radius: 999px; padding: 2px 10px; }
.ev-muted, .ev-empty { color: #6b7791; font-size: 15px; }
.ev-empty { background: #fff; border: 1.5px dashed #d6deeb; border-radius: 14px; padding: 26px; text-align: center; }

.ev-cards { display: flex; flex-direction: column; gap: 12px; }
.ev-card { background: #fff; border-radius: 14px; box-shadow: 0 4px 18px rgba(6,29,71,0.06); overflow: hidden; transition: box-shadow .15s; }
.ev-card.open { box-shadow: 0 8px 30px rgba(6,29,71,0.12); }
.ev-card-head { display: flex; align-items: center; padding: 16px 18px; gap: 12px; cursor: default; }
.ev-card-head.clickable { cursor: pointer; }
.ev-card-main { flex: 1; min-width: 0; }
.ev-card-addr { font-size: 14px; color: #5a6783; font-weight: 600; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ev-card-est { font-size: 26px; font-weight: 800; letter-spacing: -0.6px; }
.ev-card-est.estimating { color: #9aa6bd; font-size: 22px; }
.ev-card-est.failed { color: #c23b3b; font-size: 20px; }
.ev-card-range { font-size: 13px; color: #6b7791; }
.ev-card-uplift { font-size: 12.5px; font-weight: 700; color: var(--ev-accent, #12b86c); margin: 1px 0 2px; }
.ev-uplift-items { margin-top: 5px; padding: 7px 10px; background: rgba(18, 184, 108, 0.06); border-radius: 8px; display: flex; flex-direction: column; gap: 4px; }
.ev-uplift-row { display: flex; justify-content: space-between; gap: 12px; font-size: 12px; font-weight: 500; color: #1b2740; }
.ev-uplift-name { color: #47536b; }
.ev-uplift-amt { color: var(--ev-accent, #12b86c); font-weight: 700; white-space: nowrap; }
.ev-retry { color: var(--ev-accent, #12b86c); font-weight: 700; cursor: pointer; }
.ev-dots { display: inline-flex; gap: 3px; margin-left: 6px; vertical-align: middle; }
.ev-dots i { width: 5px; height: 5px; border-radius: 50%; background: #9aa6bd; display: inline-block; animation: ev-bounce 1.2s infinite ease-in-out both; }
.ev-dots i:nth-child(1) { animation-delay: -0.24s; }
.ev-dots i:nth-child(2) { animation-delay: -0.12s; }
@keyframes ev-bounce { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
.ev-spinner { width: 18px; height: 18px; border: 2.5px solid #e3eaf6; border-top-color: var(--ev-accent, #12b86c); border-radius: 50%; animation: ev-spin 0.8s linear infinite; }
@keyframes ev-spin { to { transform: rotate(360deg); } }
.ev-card-side { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.ev-conf { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.4px; }
.c-high { background: #d8f5e5; color: #0c8a4e; }
.c-medium { background: #fdf0d6; color: #b97e10; }
.c-low { background: #fbe0e0; color: #c23b3b; }
.c-refining { background: #eef2fb; color: #4a5f8f; text-transform: none; letter-spacing: 0; }
.ev-spinner.sm { width: 13px; height: 13px; border-width: 2px; }
.ev-empty-note { font-size: 13px; color: #8593ad; line-height: 1.5; }
.ev-anchor-form { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.ev-anchor-in { max-width: 150px; padding: 8px 10px; font-size: 14px; }
/* Sharpen-your-estimate panel */
/* Sharpen CTA at the top of every report */
.ev-sharpen-top { margin-bottom: 18px; }
.ev-sharpen-cta { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border: 1px solid var(--ev-accent, #12b86c); background: rgba(18, 184, 108, 0.06); border-radius: 12px; cursor: pointer; }
.ev-sharpen-cta:hover { background: rgba(18, 184, 108, 0.11); }
.ev-sharpen-ico { font-size: 22px; line-height: 1; }
.ev-sharpen-cta-txt { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.ev-sharpen-cta-txt b { font-size: 14.5px; color: #1b2740; }
.ev-sharpen-cta-txt span { font-size: 12.5px; color: #5a6783; line-height: 1.45; }
.ev-sharpen-go { flex: 0 0 auto; font-size: 13px; font-weight: 700; color: var(--ev-accent, #12b86c); white-space: nowrap; }
.ev-upgrade { border: 1px solid #eef1f7; border-radius: 10px; padding: 10px; margin-bottom: 4px; }
.ev-upgrade-detail { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #e6eaf2; }
.ev-detail-field { display: flex; flex-direction: column; gap: 3px; }
.ev-detail-field label { font-size: 11px; color: #7381a0; font-weight: 600; }
.ev-brand-edit { border: none; background: none; color: rgba(255, 255, 255, 0.5); font-size: 13px; cursor: pointer; padding: 2px 6px; }
.ev-brand-edit:hover { color: #fff; }
.ev-brand-editor { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 12px 0 2px; }
.ev-be-in { padding: 7px 10px; font-size: 13px; max-width: 220px; }
.ev-be-acc { max-width: 160px; }
.ev-up-desc { width: 100%; font-size: 14px; padding: 9px 11px; resize: vertical; box-sizing: border-box; font-family: inherit; line-height: 1.4; }
.ev-cost-field { display: flex; align-items: center; flex: 1 1 110px; max-width: 150px; border: 1px solid #d7deea; border-radius: 8px; background: #fff; }
.ev-cost-sym { padding: 0 3px 0 10px; color: #7381a0; font-size: 14px; }
.ev-cost-in { border: none; border-radius: 0; padding: 8px 10px 8px 2px; font-size: 14px; flex: 1; min-width: 0; background: none; }
.ev-cost-in:focus { outline: none; }
.ev-sharpen-note { font-size: 11.5px; color: #8593ad; line-height: 1.5; }
.ev-sharpen-hint { font-size: 11.5px; color: #a06a12; background: rgba(214, 158, 46, 0.1); border-radius: 8px; padding: 6px 10px; line-height: 1.45; margin-top: -2px; }
/* AI Q&A assistant */
/* Accuracy gate modal */
.ev-need-overlay { position: fixed; inset: 0; z-index: 60; background: rgba(11, 26, 58, 0.55); display: flex; align-items: center; justify-content: center; padding: 16px; }
.ev-need-card { width: 520px; max-width: 100%; max-height: calc(100vh - 32px); overflow-y: auto; background: #fff; border-radius: 16px; padding: 22px 22px 18px; box-shadow: 0 24px 64px rgba(20, 30, 55, 0.35); }
.ev-need-h { font-size: 18px; font-weight: 800; color: #0b1a3a; letter-spacing: -0.3px; }
.ev-need-why { font-size: 13.5px; color: #47536b; line-height: 1.5; margin-top: 8px; }
.ev-need-addr { font-size: 13px; font-weight: 700; color: #1b2740; margin: 10px 0 4px; }
.ev-need-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px; }
.ev-need-f { display: flex; flex-direction: column; gap: 5px; font-size: 12px; font-weight: 700; color: #47536b; }
.ev-need-f .ev-input { padding: 10px 12px; font-size: 15px; }
.ev-need-lot { grid-column: 1 / -1; }
.ev-need-hint { font-size: 11px; font-weight: 500; color: #8593ad; }
.ev-need-actions { display: flex; align-items: center; gap: 14px; margin-top: 16px; }
.ev-need-actions .ev-btn { padding: 11px 20px; }
.ev-need-adaptive { margin-top: 14px; padding-top: 12px; border-top: 1px solid #eef1f7; display: flex; flex-direction: column; gap: 11px; }
.ev-need-sub { font-size: 12.5px; font-weight: 700; color: #1b2740; }
.ev-need-q { display: flex; flex-direction: column; gap: 5px; font-size: 12.5px; font-weight: 600; color: #47536b; }
.ev-need-q .ev-input { padding: 10px 12px; font-size: 14px; font-weight: 400; }
.ev-need-note { font-size: 11.5px; color: #8593ad; line-height: 1.5; margin-top: 12px; }
.ev-chat { position: fixed; right: 20px; bottom: 20px; z-index: 40; }
.ev-chat-fab { display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border: none; border-radius: 26px; background: var(--ev-accent, #12b86c); color: #fff; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 20px rgba(18, 184, 108, 0.35); }
.ev-chat-fab:hover { filter: brightness(1.05); }
.ev-chat-fab-ico { font-size: 16px; }
.ev-chat-panel { width: 370px; max-width: calc(100vw - 32px); height: 520px; max-height: calc(100vh - 40px); display: flex; flex-direction: column; background: #fff; border: 1px solid #e6eaf2; border-radius: 16px; box-shadow: 0 16px 48px rgba(20, 30, 55, 0.22); overflow: hidden; }
.ev-chat-head { display: flex; align-items: center; justify-content: space-between; padding: 13px 16px; background: #0b1a3a; color: #fff; font-size: 14px; font-weight: 700; }
.ev-chat-x { border: none; background: none; color: rgba(255, 255, 255, 0.7); font-size: 15px; cursor: pointer; }
.ev-chat-x:hover { color: #fff; }
.ev-chat-body { position: relative; flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; background: #f7f9fc; }
.ev-chat-intro { font-size: 13px; color: #5a6783; line-height: 1.5; }
.ev-chat-chips { display: flex; flex-direction: column; gap: 7px; margin-top: 12px; }
.ev-chat-chip { text-align: left; padding: 9px 12px; border: 1px solid var(--ev-accent, #12b86c); background: rgba(18, 184, 108, 0.06); color: #1b2740; border-radius: 10px; font-size: 12.5px; cursor: pointer; }
.ev-chat-chip:hover { background: rgba(18, 184, 108, 0.12); }
.ev-chat-msg { max-width: 85%; padding: 9px 12px; border-radius: 12px; font-size: 13px; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word; }
.ev-chat-msg.user { align-self: flex-end; background: var(--ev-accent, #12b86c); color: #fff; border-bottom-right-radius: 4px; }
.ev-chat-msg.assistant { align-self: flex-start; background: #fff; color: #1b2740; border: 1px solid #e6eaf2; border-bottom-left-radius: 4px; }
.ev-chat-typing { display: inline-flex; gap: 4px; align-items: center; }
.ev-chat-typing span { width: 6px; height: 6px; border-radius: 50%; background: #b7c0d3; animation: evChatBlink 1.2s infinite both; }
.ev-chat-typing span:nth-child(2) { animation-delay: 0.2s; }
.ev-chat-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes evChatBlink { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }
.ev-chat-input { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #eef1f7; background: #fff; }
.ev-chat-in { flex: 1; padding: 9px 11px; font-size: 13px; }
.ev-chat-send { padding: 9px 16px; font-size: 13px; }
.ev-sharpen-form { display: flex; flex-direction: column; gap: 10px; }
.ev-sub-h { font-size: 13px; font-weight: 700; color: #1b2740; margin-top: 4px; }
.ev-sub-note { font-weight: 400; color: #8593ad; }
.ev-sharpen-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.ev-sh-in { flex: 1 1 90px; max-width: 130px; padding: 8px 10px; font-size: 14px; }
.ev-sh-type { flex: 1 1 150px; max-width: 190px; }
.ev-sh-x { padding: 6px 10px; }
.ev-sh-add { align-self: flex-start; }
.ev-sharpen-actions { display: flex; gap: 8px; margin-top: 4px; }
.ev-up-add { color: var(--ev-accent, #12b86c); }
/* YOUR HOME — labeled rows (app parity) */
.ev-kv-card { border: 1px solid #eef1f7; border-radius: 12px; padding: 2px 14px; }
.ev-kv { display: flex; justify-content: space-between; gap: 12px; padding: 11px 0; border-bottom: 1px solid #f2f4f9; font-size: 14.5px; min-height: 38px; align-items: baseline; }
.ev-kv:last-child { border-bottom: none; }
.ev-kv .k { color: #7381a0; flex: 0 0 auto; }
.ev-kv .v { color: #1b2740; font-weight: 600; text-align: right; }
.ev-lastsold { font-size: 13.5px; color: #5a6783; margin-bottom: 10px; }
.ev-lastsold b { color: #1b2740; }
/* COMPARABLE SALES/LISTINGS — side-by-side grid (app parity) */
.ev-grid-wrap { display: flex; gap: 8px; align-items: flex-start; }
.ev-grid-labels { flex: 0 0 auto; }
.ev-grid-scroll { display: flex; gap: 0; overflow-x: auto; }
.ev-gcol { flex: 0 0 100px; }
.ev-gcol.yours { background: rgba(18, 184, 108, 0.07); border-radius: 8px; }
.ev-gc { height: 30px; line-height: 30px; font-size: 12.5px; color: #1b2740; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 0 5px; }
.ev-grid-labels .ev-gc { text-align: left; width: 96px; }
.ev-gc.lbl { color: #7381a0; }
.ev-gc.name { font-weight: 700; }
.ev-gc.b { font-weight: 700; }
.ev-gc.up { color: var(--ev-accent, #12b86c); }
.ev-gc.down { color: #d84b4b; }
.ev-takeaway { font-size: 13.5px; font-weight: 600; color: #1b2740; margin-top: 12px; line-height: 1.5; }
.ev-grid-cap { font-size: 11.5px; color: #8593ad; margin-top: 8px; line-height: 1.5; }
.ev-win { color: var(--ev-accent, #12b86c); font-weight: 700; margin-left: 3px; font-size: 11px; }
.c-manual { background: #e3eaf6; color: #2c4474; }
.ev-chev { color: #9fb0cc; font-size: 11px; }
.ev-card-del { border: none; background: none; color: #b8c2d6; font-size: 15px; line-height: 1; cursor: pointer; padding: 4px 6px; border-radius: 6px; }
.ev-card-del:hover { color: #c23b3b; background: #fbe9e9; }
.ev-card-del.confirm { font-size: 12px; font-weight: 700; color: #fff; background: #c23b3b; padding: 5px 9px; }

.ev-report { padding: 4px 18px 20px; border-top: 1px solid #eef1f7; }
.ev-banner { border-radius: 10px; padding: 11px 13px; font-size: 13px; margin: 14px 0 4px; line-height: 1.45; }
.ev-banner.warn { background: #fdf0d6; color: #8a6310; }
.ev-banner.info { background: #eaf1fb; color: #355184; }
.ev-banner.refining { background: #eef2fb; color: #4a5f8f; display: flex; align-items: center; gap: 8px; }
.ev-basis { font-size: 14px; color: #475067; line-height: 1.5; margin: 14px 0 8px; }
.ev-points { margin: 0 0 4px; padding-left: 18px; }
.ev-points li { font-size: 13.5px; color: #475067; line-height: 1.5; margin-bottom: 4px; }
.ev-section { margin-top: 18px; }
.ev-section-h { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #8593ad; margin-bottom: 10px; }
.ev-comp { display: flex; align-items: baseline; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f2f4f9; }
.ev-comp-addr { flex: 1; font-size: 13.5px; font-weight: 600; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ev-comp-meta { display: flex; gap: 8px; font-size: 12px; color: #8593ad; }
.ev-comp-price { font-size: 14px; font-weight: 700; text-align: right; }
.ev-comp-adj { font-size: 11px; font-weight: 700; color: var(--ev-accent, #12b86c); margin-top: 2px; white-space: nowrap; }
.ev-subj { flex-wrap: wrap; row-gap: 4px; font-size: 13px; color: #475067; }
.ev-subj span { background: #f2f4f9; padding: 3px 9px; border-radius: 999px; font-weight: 600; }
/* Value-over-time line graph (app parity) */
.ev-line { width: 100%; height: auto; display: block; margin-top: 4px; }
.ev-line-fill { fill: var(--ev-accent, #12b86c); opacity: 0.10; }
.ev-line-stroke { fill: none; stroke: var(--ev-accent, #12b86c); stroke-width: 2.5; stroke-linejoin: round; stroke-linecap: round; }
.ev-line-dot { fill: var(--ev-accent, #12b86c); }
.ev-line-lbl { fill: #8593ad; font-size: 11px; }
.ev-report-actions { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.ev-mini { background: #eef1f7; border: none; color: #2c4474; font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 9px; cursor: pointer; }
.ev-mini:hover { background: #e3eaf6; }
.ev-mini:disabled { opacity: 0.5; }
.ev-del { color: #c23b3b; background: #fbe9e9; }
.ev-pdf { color: #fff; background: var(--ev-accent, #12b86c); }
.ev-pdf:hover { opacity: 0.9; }
.ev-del:hover { background: #f7dada; }
.ev-stamp { margin-left: auto; font-size: 12px; color: #9aa6bd; }

@media (max-width: 560px) {
  .ev-new-row { flex-direction: column; }
  .ev-btn-go { width: 100%; }
  .ev-h1 { font-size: 26px; }
}
</style>

<!-- Google Places dropdown is appended to <body>, outside this component — scoped CSS can't reach it,
     so it needs a global rule to sit above the sticky header (z-index) and match the card styling. -->
<style>
.pac-container {
  z-index: 100000 !important;
  border-radius: 10px;
  margin-top: 4px;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.16);
  border: 1px solid #e6eaf2;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
.pac-item { padding: 6px 12px; font-size: 14px; cursor: pointer; }
.pac-item:hover { background: #f4f6fa; }
</style>
